import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, KeyRound } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO from "@/lib/useSEO";

// TEAM_IMAGE — luxury Cabo living interior with photo wall
const TEAM_IMAGE = "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1800&q=85";
// ARCH_IMAGE — modern Cabo architectural staircase
const ARCH_IMAGE = "https://images.unsplash.com/photo-1518733057094-95b53143d2a7?auto=format&fit=crop&w=1800&q=85";

// =============================================================================
// TEAM PORTRAITS — Drop the uploaded portraits into /app/frontend/public/team/
// then replace the constants below with the public path, e.g. "/team/luis.jpg"
// Until then, set the URL to null to render the elegant initial-monogram placeholder.
// =============================================================================
// TEMPORARILY HIDDEN (client request) until professional PKM team photographs
// are available. Set to true to restore the full Meet the Team section
// (heading, portrait cards, vendor-network card). Stats row remains public.
const SHOW_TEAM_SECTION = false;

const TEAM1_PHOTO = null; // "/team/luis-fernando-mendoza.jpg" — formal portrait of Luis, Cabo light, navy or gold backdrop
const TEAM2_PHOTO = null; // "/team/fernando-mendoza-rocha.jpg" — formal portrait of Fernando, matching style

function PortraitCard({ photo, initials, name, title, paragraphs, index }) {
    return (
        <Reveal delay={index * 100} data-testid={`team-card-${index}`}>
            <div className="aspect-[4/5] overflow-hidden bg-navy relative">
                {photo ? (
                    <img src={photo} alt={name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "radial-gradient(circle at 30% 30%, #143f63 0%, #0B2C47 60%, #07203A 100%)" }}>
                        <div className="text-center">
                            <span className="font-serif text-gold text-[7rem] leading-none tracking-[0.1em] block">{initials}</span>
                            <span className="block w-12 h-px bg-gold/60 mx-auto mt-6" />
                            <span className="block overline text-pearl/50 mt-5 text-[0.65rem]">Portrait Coming Soon</span>
                        </div>
                    </div>
                )}
            </div>
            <div className="mt-7">
                <h3 className="font-serif text-3xl leading-tight">{name}</h3>
                <p className="overline text-gold mt-3">{title}</p>
                <span className="block w-8 h-px bg-gold my-5" />
                <div className="space-y-4 text-sm text-navy/75 leading-relaxed">
                    {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
            </div>
        </Reveal>
    );
}

function VendorCard({ name, title, paragraphs }) {
    return (
        <Reveal delay={300} data-testid="team-card-vendor">
            <div className="bg-navy text-pearl p-10 sm:p-14 grid md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                    <div className="w-16 h-16 border border-gold/60 flex items-center justify-center">
                        <KeyRound className="w-7 h-7 text-gold" strokeWidth={1.25} />
                    </div>
                    <span className="hidden md:block w-10 h-px bg-gold" />
                </div>
                <div className="md:col-span-9 space-y-5">
                    <p className="overline text-gold">{title}</p>
                    <h3 className="font-serif text-pearl text-3xl leading-tight">{name}</h3>
                    <div className="space-y-4 text-sm text-pearl/75 leading-relaxed">
                        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

export default function About() {
    const { t, lang } = useLanguage();

    useSEO({
        title: lang === "es"
            ? "Acerca de Pacific Key Management | Administración de Estates Los Cabos"
            : "About Pacific Key Management | Los Cabos Estate Management",
        description: lang === "es"
            ? "Conozca a Pacific Key Management, un equipo boutique de administración de estates que ofrece supervisión personalizada y servicio de hospitalidad en Los Cabos."
            : "Meet Pacific Key Management, a boutique estate management team providing personalized property oversight and hospitality-driven service in Los Cabos.",
        path: "/about",
        keywords: "Pacific Key Management about, Luxury Estate Management Los Cabos, Private Estate Office Cabo",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
        ],
    });

    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow={t("about.heroEyebrow")}
                title={<>{t("about.heroTitle1")} <span className="italic font-light">{t("about.heroTitle2")}</span></>}
                subtitle={t("about.heroSubtitle")}
                image="https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=2200&q=85"
            />

            <section className="bg-pearl py-24 sm:py-32">
                <div className="container-luxe grid lg:grid-cols-12 gap-16">
                    <Reveal className="lg:col-span-5 space-y-6">
                        <p className="overline text-gold">{t("about.storyEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("about.storyTitle")}</h2>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-7 space-y-6 text-navy/75 leading-relaxed">
                        <p>{t("about.storyP1")}</p>
                        <p>{t("about.storyP2")}</p>
                        <p>{t("about.storyP3")}</p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-sand/40 py-24 sm:py-32">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 items-center">
                    <Reveal className="lg:col-span-6">
                        <div className="aspect-[5/6] overflow-hidden">
                            <img src={TEAM_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-6 space-y-6">
                        <p className="overline text-gold">{t("about.founderEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("about.founderTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("about.founderP1")}</p>
                        <p className="text-navy/75 leading-relaxed">{t("about.founderP2")}</p>
                    </Reveal>
                </div>
            </section>

            {/* MEET THE TEAM — temporarily hidden via SHOW_TEAM_SECTION until
                professional photography is ready. Stats row stays visible. */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="about-team-section">
                <div className="container-luxe">
                    {SHOW_TEAM_SECTION && (
                        <>
                            <Reveal className="max-w-3xl mb-20">
                                <p className="overline text-gold">{t("about.teamEyebrow")}</p>
                                <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("about.teamTitle")}</h2>
                                <span className="gold-divider mt-6" />
                                <p className="mt-7 text-navy/70 leading-relaxed text-lg italic font-light max-w-2xl">
                                    {t("about.teamLead")}
                                </p>
                            </Reveal>

                            {/* Two-column portrait cards */}
                            <div className="grid md:grid-cols-2 gap-12 lg:gap-20 mb-16 lg:mb-20">
                                <PortraitCard
                                    index={0}
                                    photo={TEAM1_PHOTO}
                                    initials={t("about.team1Initials")}
                                    name={t("about.team1Name")}
                                    title={t("about.team1Title")}
                                    paragraphs={[
                                        t("about.team1P1"),
                                        t("about.team1P2"),
                                        t("about.team1P3"),
                                        t("about.team1P4"),
                                    ]}
                                />
                                <PortraitCard
                                    index={1}
                                    photo={TEAM2_PHOTO}
                                    initials={t("about.team2Initials")}
                                    name={t("about.team2Name")}
                                    title={t("about.team2Title")}
                                    paragraphs={[
                                        t("about.team2P1"),
                                        t("about.team2P2"),
                                        t("about.team2P3"),
                                    ]}
                                />
                            </div>

                            {/* Vendor Network — third card, full width, no photo */}
                            <VendorCard
                                name={t("about.team3Name")}
                                title={t("about.team3Title")}
                                paragraphs={[
                                    t("about.team3P1"),
                                    t("about.team3P2"),
                                ]}
                            />
                        </>
                    )}

                    {/* Stats row */}
                    <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border-t border-b border-navy/10 ${SHOW_TEAM_SECTION ? "mt-16 lg:mt-20" : ""}`}>
                        {["1", "2", "3", "4"].map((n, i) => (
                            <Reveal key={n} delay={i * 80} className="bg-pearl px-8 py-10 text-center" data-testid={`about-stat-${i}`}>
                                <p className="font-serif text-gold text-4xl sm:text-5xl leading-none">{t(`about.stat${n}Number`)}</p>
                                <span className="block w-8 h-px bg-gold/60 mx-auto my-5" />
                                <p className="text-xs sm:text-[0.78rem] tracking-[0.18em] uppercase text-navy/70 leading-relaxed">{t(`about.stat${n}Label`)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sand/40 py-24 sm:py-32">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("about.valuesEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("about.valuesTitle")}</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {["v1", "v2", "v3", "v4"].map((k, i) => (
                            <Reveal key={k} delay={i * 80} className="bg-pearl p-10 min-h-[260px]" data-testid={`about-value-${i}`}>
                                <span className="text-gold font-serif text-2xl">0{i + 1}</span>
                                <h3 className="mt-6 font-serif text-2xl">{t(`about.${k}.title`)}</h3>
                                <span className="block w-8 h-px bg-gold my-4" />
                                <p className="text-sm text-navy/70 leading-relaxed">{t(`about.${k}.body`)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-navy text-pearl py-24 sm:py-32">
                <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-7 space-y-6">
                        <p className="overline text-gold">{t("about.standardsEyebrow")}</p>
                        <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("about.standardsTitle")}</h2>
                        <p className="text-pearl/75 leading-relaxed max-w-xl">{t("about.standardsBody")}</p>
                        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mt-8">
                            {["s1", "s2", "s3", "s4"].map((k) => (
                                <div key={k}>
                                    <p className="overline text-gold mb-2">{t(`about.${k}.label`)}</p>
                                    <p className="text-pearl/75 text-sm leading-relaxed">{t(`about.${k}.body`)}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img src={ARCH_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-sand/40 py-20">
                <div className="container-luxe text-center max-w-2xl mx-auto">
                    <h2 className="font-serif text-3xl sm:text-4xl mb-6">{t("about.outroTitle")}</h2>
                    <p className="text-navy/70 mb-8">{t("about.outroBody")}</p>
                    <Link to="/contact" className="btn-luxe-primary" data-testid="about-cta-contact">
                        {t("common.requestConsultation")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
